import React from "react";
import nr from "newrelic";
import type { NextParsedUrlQuery } from "next/dist/server/request-meta";
import type {
    GetServerSidePropsContext,
    GetServerSidePropsResult,
} from "next/types";
import { Provider, useDispatch, useSelector } from "react-redux";
import { ContainerWithParallaxLeaderboard } from "@bloomberg/media-ui-ads";
import {
    BannerData,
    MenuItems,
    SubmenuItem,
} from "@bloomberg/media-ui-nav/dist/types";
import Logger from "@foundation/logger";
import { BREAKING_NEWS_REGIONS } from "@foundation/nav";
import { RegUIClientProvider } from "@foundation/reg-ui-client";
import { parseGeoInfo } from "@foundation/sparkle/lib/adDials/parseGeoInfo";
import AdOptionsProvider from "@foundation/sparkle/lib/AdOptionsProvider";
import WinSparkleProvider from "@foundation/sparkle/lib/WinSparkleProvider";
import { TophatResponse } from "@foundation/tophat-client/lib/types";

import { handlePageError } from "~api/errorHandler";
import { fetchEditorialBoard } from "~api/fetchEditorialBoard";
import { fetchStoryBySlug } from "~api/fetchStory";
import { AdConfig, getAdConfig } from "~api/getAdConfig";
import { AbbaProviderWithUserInfo } from "~components/AbbaProviderWithUserInfo";
import { ContentMetadata } from "~components/ContentMetadata";
import ErrorBoundary from "~components/ErrorBoundary";
import ArticleLayout from "~components/Layout/articleLayout";
import {
    PageLayout,
    PageLayoutChildrenProps,
} from "~components/Layout/pageLayout";
import { PlugScript, plugSupportedType } from "~components/Plug";
import { NewSearchTip } from "~components/search/popover/NewSearchTip";
import {
    createUnexpectedParamsError,
    InvalidTranslationError,
    TranslationRateLimitingError,
} from "~errors/errors";
import { selectStories } from "~redux/features/stories-slice";
import { updateCurrentSlug } from "~redux/features/transporter-slice";
import configureAppStore from "~redux/store";
import {
    Story as StoryType,
    OPINION_ARTICLE_TYPE,
    Story,
    getAnyCollection,
} from "~types/types";
import { Locale } from "~types/wardenclyffe";
import { insertStoryAds } from "~utils/ads/insertAds";
import { getAdTargeting } from "~utils/AdTargetingClient";
import {
    getBreakingNews,
    getFooterMenuItems,
    getMenuItems,
    getTophat,
} from "~utils/commonPageResources";
import getArticleMetadata from "~utils/getArticleMetadata";
import getArticleType from "~utils/getArticleType";
import { getGtmTrackingData } from "~utils/getGtmTrackingData";
import getLanguagePreference from "~utils/getLanguagePreference";
import { getOpinionArticleType } from "~utils/getOpinionArticleType";
import { getTranslatedMessages } from "~utils/i18n/getTranslatedMessages";
import { isInvalidSlug } from "~utils/isInvalidSlug";
import isOpinion from "~utils/isOpinion";
import { getCurrentUrl } from "~utils/nextContextUtil";
import { constructSlug } from "~utils/slugUtil";
import {
    DEFAULT_PAGE_TYPE,
    getDecoupledAdOptions,
    getSparkleProviderOptions,
    sparkleAdOptions,
} from "~utils/sparkleOptions";
import { WardenclyffeUser } from "~utils/wardenclyffe";

export type StoryPropTypes = {
    story: StoryType;
};

export interface StoryPageProps {
    preview: boolean;
    story: StoryPropTypes["story"];
    layout: {
        tophat: TophatResponse | null;
        menuItems: MenuItems;
        footerMenuItems: Record<string, SubmenuItem>;
        breakingNews: BannerData;
    };
    translatedMessages: Record<string, string>;
    adConfig: AdConfig;
}

export interface StoryPageParams extends NextParsedUrlQuery {
    language: string;
    type: string;
    slug: string[];
}

export interface StoryPageQuery extends NextParsedUrlQuery {}

export interface StoryPageGetServerSidePropsContext
    extends GetServerSidePropsContext {
    params: StoryPageParams;
    query: StoryPageQuery;
    locale: Locale;
}

type StoryPageGetServerSideProps<P extends StoryPageProps = StoryPageProps> = (
    context: StoryPageGetServerSidePropsContext
) => Promise<GetServerSidePropsResult<P>> | { notFound: true };

export const getServerSideProps: StoryPageGetServerSideProps | unknown = async (
    context: StoryPageGetServerSidePropsContext
) => {
    const {
        params,
        query,
        req: { cookies },
        res,
        locale,
    } = context;
    const { language = "en", type } = params;
    const region = cookies.exp_pref as BREAKING_NEWS_REGIONS;
    const countryCode = cookies.country_code || "US";
    const languagePreference = getLanguagePreference(cookies);
    const slug = constructSlug(params.slug);
    const geoInfo = parseGeoInfo(cookies.geo_info);

    nr.addCustomAttributes({
        ...query,
        slug,
        language,
        type,
    });

    if (isInvalidSlug(slug)) {
        const error = createUnexpectedParamsError(
            `Invalid slug for article page: ${slug}`
        );

        nr.noticeError(error, true);
        console.warn(error);

        return { notFound: true };
    }

    Logger.info(`${slug} - Lightsaber`);

    let story: Story;

    try {
        try {
            story = await fetchStoryBySlug({
                language,
                slug,
                languagePreference,
                /**
                 * [EC] 08/12 - if locale is en/US website, we need to use xlang user to override default
                 * This is currently a business requirement to allow rendering non-english, cross-wired articles
                 * */
                userOptions: {
                    locale,
                    ...(locale === Locale.En && {
                        user: WardenclyffeUser.xlang,
                    }),
                },
            });
        } catch (error) {
            if (
                !(
                    error instanceof TranslationRateLimitingError ||
                    error instanceof InvalidTranslationError
                )
            ) {
                throw error;
            }
            // try again with the error
            story = await fetchStoryBySlug({
                language,
                slug,
                languagePreference,
                /**
                 * [EC] 08/12 - if locale is en/US website, we need to use xlang user to override default
                 * This is currently a business requirement to allow rendering non-english, cross-wired articles
                 * */
                userOptions: {
                    locale,
                    ...(locale === Locale.En && {
                        user: WardenclyffeUser.xlang,
                    }),
                },
                translationErrors: error,
            });

            res.setHeader(
                "Cache-Control",
                "public, max-age=0, must-revalidate"
            );
        }
    } catch (error) {
        return handlePageError({
            error,
            res,
            newrelicParams: {
                language,
                slug,
                type,
                route: "content/[lang]/news/[type]/[...slug]",
            },
        });
    }

    const { authors } = story;
    const contextualNav = getAnyCollection(story);

    if (isOpinion(story) && authors) {
        story.opinionArticleType = getOpinionArticleType(authors);
    }

    const pageId = story?.adTargeting?.page
        ? `lightsaber-${story?.adTargeting?.page}`
        : DEFAULT_PAGE_TYPE;

    return Promise.all([
        getTophat(),
        getBreakingNews(region),
        getFooterMenuItems(),
        getMenuItems(),
        story.opinionArticleType === OPINION_ARTICLE_TYPE.EDITORIAL_BOARD &&
            fetchEditorialBoard(),
        getAdTargeting({
            pageId,
            slug,
            adPageType: "article",
            url: getCurrentUrl(context),
        }),
        getTranslatedMessages({ locale }),
    ])
        .then(
            ([
                tophat,
                breakingNews,
                footerMenuItems,
                menuItems,
                editorialBoard,
                adTargeting,
                translatedMessages,
            ]) => {
                const adConfig = getAdConfig({
                    adCode: adTargeting.adCode as string,
                    ...geoInfo,
                });
                insertStoryAds(story, adConfig.adDemand);

                if (story.adTargeting && adTargeting) {
                    story.adTargeting = {
                        ...story.adTargeting,
                        ...adTargeting,
                    };
                }

                if (story.adTargeting && contextualNav?.slug) {
                    story.adTargeting.record = contextualNav.slug;
                }

                if (editorialBoard) {
                    story.editorialBoard = editorialBoard;
                }

                // isPageTracked is used to keep track of whether we've recorded the page view
                // in GA and parse.ly
                // Since we're doing the first story's dataLayer update in the nextjs-gtm lib
                // We want to mark the story as having been tracked
                story.isPageTracked = true;

                const analytics = {
                    ...getGtmTrackingData(story),
                    referrer: story.url,
                    transporter: false,
                };

                return {
                    props: {
                        story,
                        preview: false,
                        analytics,
                        layout: {
                            tophat,
                            menuItems,
                            footerMenuItems,
                            breakingNews,
                        },
                        countryCode,
                        adConfig,
                        translatedMessages,
                    },
                };
            }
        )
        .catch((error) =>
            handlePageError({
                error,
                res,
                newrelicParams: {
                    language,
                    slug,
                    type,
                    route: "content/[lang]/news/[type]/[...slug]",
                },
            })
        );
};

const StoryPage = ({ preview, adConfig }: StoryPageProps) => {
    const stories = useSelector(selectStories);
    const dispatch = useDispatch();

    return (
        <>
            {stories.map((story: Story) => {
                if (!story) {
                    console.error(
                        `Invalid story in stories list: ${JSON.stringify(
                            stories
                        )}`
                    );
                    return null;
                }

                const onEnterViewport = () => {
                    dispatch(updateCurrentSlug(story));
                };

                return (
                    <ArticleLayout
                        onEnterViewport={onEnterViewport}
                        key={story.id}
                        story={story}
                        preview={preview}
                        adConfig={adConfig}
                    />
                );
            })}
            <NewSearchTip />
        </>
    );
};

StoryPage.getLayout = function getLayout(
    page: PageLayoutChildrenProps & { props: StoryPageProps }
) {
    const { story, adConfig } = page.props;

    const store = configureAppStore({
        stories: {
            storiesList: [story],
        },
        transporter: { currentSlug: story.slug, hasTransported: false },
    });
    const sparkleOptions = getSparkleProviderOptions(story);
    const type = getArticleType(story);
    const { isDecoupled, adOptions } = getDecoupledAdOptions();

    return (
        <>
            <Provider store={store}>
                <ContentMetadata story={story} type={type} />
                <RegUIClientProvider>
                    <WinSparkleProvider {...sparkleOptions}>
                        <AdOptionsProvider
                            adOptions={sparkleAdOptions(story, adConfig)}
                            articleMetadata={getArticleMetadata(story)}
                            customRefreshOptions={adConfig.customRefreshOptions}
                        >
                            <ContainerWithParallaxLeaderboard
                                adOptions={adOptions}
                                isDecoupled={isDecoupled}
                            >
                                <AbbaProviderWithUserInfo>
                                    <PageLayout type={type} props={page.props}>
                                        {/* eslint-disable-next-line */}
                                        {/* @ts-ignore the typing error for React.Component needing type and key in order to be valid. This is a NextJS typing bug */}
                                        {page}
                                    </PageLayout>
                                    {plugSupportedType(type) && (
                                        <ErrorBoundary>
                                            <PlugScript />
                                        </ErrorBoundary>
                                    )}
                                </AbbaProviderWithUserInfo>
                            </ContainerWithParallaxLeaderboard>
                        </AdOptionsProvider>
                    </WinSparkleProvider>
                </RegUIClientProvider>
            </Provider>
        </>
    );
};

export default StoryPage;
